import React, { useEffect, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';

/**
 * Versions-Anzeige in der Fussleiste jeder Seite.
 *
 * Fetcht einmalig ``/api/v1/meta/version`` und zeigt unten links
 * z.B. ``v329``. Tooltip enthaelt den Parent-Commit und den Build-
 * Zeitstempel.
 *
 * Die Versionszahl kommt aus ``backend/VERSION`` — der Git-Pre-Commit-
 * Hook (``scripts/update-version.sh``) erhoeht sie automatisch bei
 * jedem Commit. So sieht man auf einen Blick ob die Produktion noch
 * auf dem Stand des lokalen HEAD ist.
 */

interface VersionInfo {
  version: string;
  count: number;
  parent_commit: string;
  updated_at: string;
  branch: string;
}

const VersionBadge: React.FC = () => {
  const [info, setInfo] = useState<VersionInfo | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Eigener fetch — unabhaengig von apiFetch, damit der Badge auch
    // auf dem Login-Screen ohne JWT funktioniert.
    fetch('/api/v1/meta/version', { credentials: 'same-origin' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        return r.json() as Promise<VersionInfo>;
      })
      .then((data) => {
        if (!cancelled) setInfo(data);
      })
      .catch((e) => {
        if (!cancelled) setErr(String(e));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const label = info?.version ?? (err ? '?' : '…');

  const tooltipLines: string[] = [];
  if (info) {
    tooltipLines.push(`Version: ${info.version}`);
    tooltipLines.push(`Parent-Commit: ${info.parent_commit}`);
    tooltipLines.push(`Branch: ${info.branch}`);
    if (info.updated_at) tooltipLines.push(`Gebaut: ${info.updated_at}`);
  } else if (err) {
    tooltipLines.push(`Version konnte nicht geladen werden (${err})`);
  }

  return (
    <Tooltip
      title={
        <Box sx={{ whiteSpace: 'pre-line' }}>{tooltipLines.join('\n')}</Box>
      }
      arrow
      placement="top-start"
    >
      <Box
        sx={{
          position: 'fixed',
          left: 8,
          bottom: 6,
          zIndex: (t) => t.zIndex.tooltip - 1,
          px: 1,
          py: 0.25,
          borderRadius: 1,
          backgroundColor: 'action.hover',
          color: 'text.secondary',
          fontFamily: 'monospace',
          fontSize: '0.72rem',
          pointerEvents: 'auto',
          userSelect: 'none',
        }}
        data-testid="version-badge"
      >
        <Typography
          variant="caption"
          component="span"
          sx={{ fontFamily: 'inherit', fontSize: 'inherit' }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default VersionBadge;
